import {
  CallState,
  type ExternalResource,
  type HtsAirCfarOfferSelection,
  type HtsAirCfarOffersGroup,
  type HtsAirCfarOffer,
  SelectedStatus,
  type SyntheticHtsAirCfarOffer,
} from "@b2bportal/core-types";
import {
  type PayloadAction,
  type UnknownAction,
  createAsyncThunk,
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
} from "@reduxjs/toolkit";

import type { FetchBatchCfarOffers } from "../../hooks/createUseCfarFaresSource";
import {
  type FetchHtsAirCfarOffersRequest,
  HTS_AIR_CFAR_THUNK_PREFIX,
} from "../../thunks";
import type { SelectableOffersState } from "../SelectableOffersState";

export interface HtsAirCfarPassengerCounts {
  adultsCount: number;
  childrenCount: number;
  infantsInSeatCount: number;
  infantsOnLapCount: number;
}

export type HtsAirCfarFetchRequest = FetchHtsAirCfarOffersRequest;

export interface HtsAirCfarOffersState
  extends SelectableOffersState<
    HtsAirCfarFetchRequest,
    ExternalResource<HtsAirCfarOffersGroup[]>,
    HtsAirCfarOfferSelection
  > {
  roundTripOutboundSyntheticSelected?: boolean;
  roundTripOutboundSyntheticCoveragePercentage?: number | null;
  shouldShowOnlySyntheticFares?: boolean;
  returnSliceBatchCfarOffers?: ExternalResource<
    Record<string, HtsAirCfarOffer[]>
  >;
}

export const initialHtsAirCfarOffersState: HtsAirCfarOffersState = {
  fetchRequest: undefined,
  fetchCallState: CallState.NotCalled,
  offersState: { state: CallState.NotCalled },
  selectedOffer: { status: SelectedStatus.UNAVAILABLE },
};

export const fetchReturnSliceBatchCfarOffers = createAsyncThunk(
  "htsAirCfarOffers/fetchReturnSliceBatchCfarOffers",
  async (payload: {
    fareTrips: { tripId: string; fareId: string }[];
    passengers: Record<string, number>;
    funnelId?: string;
    fetchBatchCfarOffers: FetchBatchCfarOffers;
  }) => {
    const { fareTrips, passengers, funnelId, fetchBatchCfarOffers } = payload;
    const offers = await fetchBatchCfarOffers({
      fareTrips,
      passengers,
      funnelId,
    });
    return offers;
  }
);

const isHtsAirCfarThunkAction = (action: { type: string }) =>
  action.type.startsWith(HTS_AIR_CFAR_THUNK_PREFIX);

export const htsSlice = createSlice({
  name: "htsAirCfarOffers",
  initialState: initialHtsAirCfarOffersState,
  reducers: {
    setSelectedOffer: (
      state,
      action: PayloadAction<HtsAirCfarOfferSelection>
    ) => {
      state.selectedOffer = action.payload;
    },
    initFromSyntheticSelection: (
      state,
      action: PayloadAction<{
        tripId: string;
        fareId: string;
        passengers: Record<string, number>;
        cfarOffer: SyntheticHtsAirCfarOffer;
        funnelId?: string;
      }>
    ) => {
      state.fetchRequest = {
        tripId: action.payload.tripId,
        fareId: action.payload.fareId,
        passengers: action.payload.passengers,
        funnelId: action.payload.funnelId,
      };
      state.fetchCallState = CallState.Success;
      state.offersState = { state: CallState.Success, data: [] };
      state.selectedOffer = {
        status: SelectedStatus.SELECTED,
        offer: action.payload.cfarOffer,
      };
    },
    setRoundTripOutboundSynthetic: (
      state,
      action: PayloadAction<{
        selected: boolean;
        coveragePercentage: number | null;
      }>
    ) => {
      state.roundTripOutboundSyntheticSelected = action.payload.selected;
      state.roundTripOutboundSyntheticCoveragePercentage =
        action.payload.coveragePercentage;
    },
    setShouldShowOnlySyntheticFares: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.shouldShowOnlySyntheticFares = action.payload;
    },
    reset: () => initialHtsAirCfarOffersState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReturnSliceBatchCfarOffers.pending, (state) => {
        state.returnSliceBatchCfarOffers = {
          state: CallState.InProcess,
        };
      })
      .addCase(fetchReturnSliceBatchCfarOffers.fulfilled, (state, action) => {
        state.returnSliceBatchCfarOffers = {
          state: CallState.Success,
          data: action.payload,
        };
      })
      .addCase(fetchReturnSliceBatchCfarOffers.rejected, (state) => {
        state.returnSliceBatchCfarOffers = {
          state: CallState.Failed,
          error: "Failed to fetch return slice batch CFAR offers",
        };
      })
      // Existing matchers for HTS CFAR thunk actions
      .addMatcher(
        (
          action
        ): action is UnknownAction & {
          meta: { arg: HtsAirCfarFetchRequest };
        } => isPending(action) && isHtsAirCfarThunkAction(action),
        (state, action) => {
          state.fetchRequest = action.meta.arg;
          state.fetchCallState = CallState.InProcess;
          state.offersState = { state: CallState.InProcess };
          state.selectedOffer = { status: SelectedStatus.UNAVAILABLE };
        }
      )
      .addMatcher(
        (action) => isRejected(action) && isHtsAirCfarThunkAction(action),
        (state) => {
          state.fetchCallState = CallState.Failed;
          state.offersState = {
            state: CallState.Failed,
            error: "Failed to fetch CFAR offers",
          };
          state.selectedOffer = { status: SelectedStatus.UNAVAILABLE };
        }
      )
      .addMatcher(
        (action): action is PayloadAction<HtsAirCfarOffersGroup[]> =>
          isFulfilled(action) && isHtsAirCfarThunkAction(action),
        (state, action) => {
          state.fetchCallState = CallState.Success;
          state.offersState = {
            state: CallState.Success,
            data: action.payload,
          };
          state.selectedOffer = hasAvailableOffers(action.payload)
            ? { status: SelectedStatus.UNSELECTED }
            : { status: SelectedStatus.UNAVAILABLE };
        }
      );
  },
});

export const HtsAirCfarOffersActions = {
  ...htsSlice.actions,
  fetchReturnSliceBatchCfarOffers,
};

const hasAvailableOffers = (groups: HtsAirCfarOffersGroup[]) =>
  groups.some((group) => group.offers.length > 0);
